import React, { useMemo } from "react";
import { asteroidCandidates } from "../data/snapshots.js";
import { generatedAsteroidCandidateEvidenceLedger } from "../data/generatedAsteroidCandidateEvidenceLedger.js";
import { generatedCandidateCompositionEvidenceDocket } from "../data/generatedCandidateCompositionEvidenceDocket.js";
import { generatedCandidateSbdbRefreshManifest } from "../data/generatedCandidateSbdbRefreshManifest.js";
import { generatedCandidateSourceClosurePackets } from "../data/generatedCandidateSourceClosurePackets.js";
import { generatedCandidateSourcePromotionLedger } from "../data/generatedCandidateSourcePromotionLedger.js";
import { generatedCandidateSourceReviewDocket } from "../data/generatedCandidateSourceReviewDocket.js";
import { generatedLiveSmallBodyReviewQueue } from "../data/generatedLiveSmallBodyReviewQueue.js";
import { generatedSmallBodySourceCandidateLedger } from "../data/generatedSmallBodySourceCandidateLedger.js";
import { asteroidCandidateScore, round } from "../science/kernel.mjs";
import { DataTable, EvidencePanel, Metric, PageHeader, ScoreBarCell, StackList, StatusPill } from "../components/ui.jsx";

export function AsteroidScanner() {
  const evidenceByCandidateId = useMemo(
    () =>
      new Map(
        generatedAsteroidCandidateEvidenceLedger.candidateRecords.map((record) => [
          record.candidateId,
          record,
        ])
      ),
    []
  );
  const scored = useMemo(
    () =>
      asteroidCandidates
        .map((candidate) => ({
          ...candidate,
          score: asteroidCandidateScore(candidate.inputs),
          evidence: evidenceByCandidateId.get(candidate.id),
        }))
        .sort((a, b) => b.score - a.score),
    [evidenceByCandidateId]
  );

  return (
    <section className="module">
      <PageHeader
        title="ASTEROID SCANNER"
        muted="Feedstock hypothesis"
        subtitle="Synthetic candidates are placeholders until SBDB/CAD snapshot promotion."
      />
      <div className="metric-grid-4">
        <Metric label="Candidates" value={generatedAsteroidCandidateEvidenceLedger.candidateCount} />
        <Metric label="Blocked candidates" value={generatedAsteroidCandidateEvidenceLedger.blockedCandidateCount} />
        <Metric label="Evidence types" value={generatedAsteroidCandidateEvidenceLedger.requiredEvidenceTypes.length} />
        <Metric label="Ledger hash" value={generatedAsteroidCandidateEvidenceLedger.ledgerHash.slice(0, 18)} />
        <Metric label="CAD geometry screens" value={generatedSmallBodySourceCandidateLedger.geometryCandidateCount} />
        <Metric label="CAD source rows" value={generatedSmallBodySourceCandidateLedger.sourceRowCount} />
        <Metric label="Payload state" value={generatedSmallBodySourceCandidateLedger.payloadState} />
        <Metric
          label="Composition gate"
          value={generatedSmallBodySourceCandidateLedger.resourcePromotionAllowed ? "open" : "blocked"}
        />
        <Metric label="Review queue" value={generatedLiveSmallBodyReviewQueue.reviewCandidateCount} />
        <Metric label="Review state" value={generatedLiveSmallBodyReviewQueue.queueState} />
        <Metric label="Customer use" value={generatedLiveSmallBodyReviewQueue.customerUseAllowed ? "allowed" : "blocked"} />
        <Metric label="Capture state" value={generatedLiveSmallBodyReviewQueue.captureState} />
        <Metric label="Source work orders" value={generatedAsteroidCandidateEvidenceLedger.candidateSourceWorkOrderCount} />
        <Metric label="Source custody" value={generatedCandidateSourcePromotionLedger.sourceCustodyPromotedCount} />
        <Metric label="Candidate reports" value={generatedCandidateSourcePromotionLedger.reportCount} />
        <Metric label="Resource promotion" value={generatedCandidateSourcePromotionLedger.resourcePromotionAllowed ? "open" : "blocked"} />
        <Metric label="Closure packets" value={generatedCandidateSourceClosurePackets.packetCount} />
        <Metric label="Closure state" value={generatedCandidateSourceClosurePackets.sourceCustodyReady ? "ready" : "blocked"} />
        <Metric label="Resource packets" value={generatedCandidateSourceClosurePackets.resourcePromotionBlockedCount} />
        <Metric label="Closure hash" value={generatedCandidateSourceClosurePackets.packetHash.slice(0, 18)} />
        <Metric label="Review docket" value={generatedCandidateSourceReviewDocket.reviewItemCount} />
        <Metric label="Docket state" value={generatedCandidateSourceReviewDocket.reviewState} />
        <Metric label="Draft intakes" value={generatedCandidateSourceReviewDocket.draftIntakeCount} />
        <Metric label="No-intake items" value={generatedCandidateSourceReviewDocket.noIntakeCount} />
        <Metric label="SBDB missing" value={generatedCandidateSbdbRefreshManifest.missingSbdbCount} />
        <Metric label="SBDB ready drafts" value={generatedCandidateSbdbRefreshManifest.readyDraftCount} />
        <Metric label="Composition docket" value={generatedCandidateCompositionEvidenceDocket.acquisitionItemCount} />
        <Metric label="Archive targets" value={generatedCandidateCompositionEvidenceDocket.archiveTargetCount} />
        <Metric label="Composition state" value={generatedCandidateCompositionEvidenceDocket.docketState} />
        <Metric label="No-evidence receipts" value={generatedCandidateCompositionEvidenceDocket.noEvidenceReceiptRequiredCount} />
      </div>
      <EvidencePanel
        title="Candidate SBDB refresh manifest"
        status={generatedCandidateSbdbRefreshManifest.allWorkOrdersHaveSbdb ? "refresh complete" : "refresh required"}
        tone="red"
        className="panel-large"
      >
        <p>{generatedCandidateSbdbRefreshManifest.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedCandidateSbdbRefreshManifest.items.slice(0, 10)}
          getRowKey={(item) => item.refreshItemHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "sourceState", header: "Source state" },
            {
              key: "cad",
              header: "CAD",
              render: (item) => (item.hasCadRecord ? "captured" : "missing"),
            },
            {
              key: "sbdb",
              header: "SBDB",
              render: (item) => (item.hasSbdbRecord ? "captured" : "missing"),
            },
            { key: "nextAction", header: "Next action" },
            { key: "expectedPayloadPath", header: "Expected payload" },
          ]}
        />
        <StackList
          items={generatedCandidateSbdbRefreshManifest.items.filter((item) => !item.hasSbdbRecord).slice(0, 3)}
          getKey={(item) => item.refreshItemHash}
          getTitle={(item) => `${item.designation}: ${item.sourceState}`}
          getMeta={(item) => item.promotionBoundary}
          getBody={(item) => item.sbdbUrl}
        />
        <p>Refresh command: {generatedCandidateSbdbRefreshManifest.recommendedRefreshCommand}</p>
        <p>Manifest hash: {generatedCandidateSbdbRefreshManifest.manifestHash}</p>
      </EvidencePanel>
      <EvidencePanel
        title="Candidate composition evidence docket"
        status={generatedCandidateCompositionEvidenceDocket.docketState}
        tone="red"
        className="panel-large"
      >
        <p>{generatedCandidateCompositionEvidenceDocket.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedCandidateCompositionEvidenceDocket.acquisitionItems.slice(0, 10)}
          getRowKey={(item) => item.acquisitionItemHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "compositionLane", header: "Lane" },
            {
              key: "archives",
              header: "Archive targets",
              render: (item) => item.archiveTargets.map((target) => target.sourceId).join(", "),
            },
            {
              key: "rejected",
              header: "Rejected evidence",
              render: (item) => item.evidencePacketContract.rejectedEvidence.slice(0, 2).join(", "),
            },
            { key: "nextAction", header: "Next action" },
            {
              key: "command",
              header: "Command",
              render: (item) => item.commandSequence[0] ?? "not declared",
            },
          ]}
        />
        <StackList
          items={generatedCandidateCompositionEvidenceDocket.acquisitionItems.slice(0, 3)}
          getKey={(item) => item.acquisitionItemHash}
          getTitle={(item) => `${item.designation}: ${item.archiveTargets.length} archive targets`}
          getMeta={(item) => item.noEvidenceClosureRule}
          getBody={(item) =>
            item.archiveTargets
              .map((target) => `${target.sourceId}: ${target.plannedSearchTerms.slice(0, 2).join(", ")}`)
              .join(" | ")
          }
        />
        <p>Docket hash: {generatedCandidateCompositionEvidenceDocket.docketHash}</p>
      </EvidencePanel>
      <EvidencePanel
        title="Candidate source review docket"
        status={generatedCandidateSourceReviewDocket.reviewState}
        tone="red"
        className="panel-large"
      >
        <p>{generatedCandidateSourceReviewDocket.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedCandidateSourceReviewDocket.reviewItems.slice(0, 10)}
          getRowKey={(item) => item.reviewItemHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "reviewLane", header: "Review lane" },
            {
              key: "querySourceGaps",
              header: "Query gaps",
              render: (item) => item.querySourceGaps.join(", ") || "none",
            },
            { key: "requiredClosureItemCount", header: "Open gates" },
            { key: "nextAction", header: "Next action" },
            {
              key: "command",
              header: "Command",
              render: (item) => item.commandSequence[0] ?? "not declared",
            },
          ]}
        />
        <p>Docket hash: {generatedCandidateSourceReviewDocket.docketHash}</p>
      </EvidencePanel>
      <EvidencePanel
        title="Candidate source promotion ledger"
        status={generatedCandidateSourcePromotionLedger.promotionState}
        tone="red"
        className="panel-large"
      >
        <p>{generatedCandidateSourcePromotionLedger.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedCandidateSourcePromotionLedger.workOrderGaps.slice(0, 8)}
          getRowKey={(workOrder) => workOrder.workOrderHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "reviewPriorityScore", header: "Priority" },
            { key: "reason", header: "Blocked reason" },
            {
              key: "payload",
              header: "Expected payload",
              render: (workOrder) => workOrder.outputContract?.payloadPath ?? "not declared",
            },
            {
              key: "command",
              header: "Verification",
              render: (workOrder) => workOrder.outputContract?.verificationCommand ?? "not declared",
            },
          ]}
        />
        <p>Ledger hash: {generatedCandidateSourcePromotionLedger.ledgerHash}</p>
      </EvidencePanel>
      <EvidencePanel
        title="Candidate source closure packets"
        status={generatedCandidateSourceClosurePackets.sourceCustodyReady ? "source custody ready" : "source custody blocked"}
        tone="red"
        className="panel-large"
      >
        <p>{generatedCandidateSourceClosurePackets.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedCandidateSourceClosurePackets.packets.slice(0, 6)}
          getRowKey={(packet) => packet.packetHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "intakeState", header: "Intake state" },
            {
              key: "blockers",
              header: "Current blockers",
              render: (packet) => packet.currentBlockers.join(" "),
            },
            {
              key: "gaps",
              header: "Query gaps",
              render: (packet) => packet.querySourceGaps.join(", "),
            },
            {
              key: "nextCommand",
              header: "Next command",
              render: (packet) => packet.nextCommandSequence[0],
            },
          ]}
        />
        <StackList
          items={generatedCandidateSourceClosurePackets.packets.slice(0, 3)}
          getKey={(packet) => packet.packetHash}
          getTitle={(packet) => `${packet.designation}: ${packet.requiredClosureItems.length} closure gates`}
          getMeta={(packet) => packet.promotionBoundary}
          getBody={(packet) =>
            packet.requiredClosureItems
              .slice(0, 4)
              .map((item) => `${item.gate}: ${item.title}`)
              .join(" | ")
          }
        />
        <p>Packet hash: {generatedCandidateSourceClosurePackets.packetHash}</p>
      </EvidencePanel>
      <EvidencePanel title="Live candidate review queue" status="source review required" tone="red" className="panel-large">
        <p>{generatedLiveSmallBodyReviewQueue.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedLiveSmallBodyReviewQueue.reviewCandidates.slice(0, 10)}
          getRowKey={(candidate) => candidate.reviewCandidateHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "bestCloseApproachDate", header: "Best approach" },
            { key: "reviewPriorityScore", header: "Priority" },
            { key: "geometryScore", header: "Geometry" },
            {
              key: "missingEvidence",
              header: "Missing evidence",
              render: (candidate) => candidate.missingEvidence.join(", "),
            },
            {
              key: "allowedUse",
              header: "Allowed use",
              render: (candidate) => candidate.promotionGate.allowedUse,
            },
          ]}
        />
        <p>Queue hash: {generatedLiveSmallBodyReviewQueue.queueHash}</p>
      </EvidencePanel>
      <EvidencePanel title="Candidate source work orders" status="resource promotion blocked" tone="red" className="panel-large">
        <p>
          Work orders define candidate-specific SBDB, CAD, MPC, taxonomy, spectra, archive, and source-use evidence
          required before any feedstock claim can move beyond geometry screening.
        </p>
        <DataTable
          shell="shell"
          rows={generatedAsteroidCandidateEvidenceLedger.candidateSourceWorkOrders.slice(0, 6)}
          getRowKey={(workOrder) => workOrder.workOrderHash}
          columns={[
            { key: "queueRank", header: "Rank" },
            { key: "designation", header: "Designation" },
            { key: "reviewPriorityScore", header: "Priority" },
            {
              key: "queries",
              header: "Query targets",
              render: (workOrder) =>
                workOrder.sourceQueryTargets.map((target) => target.sourceId).join(", "),
            },
            {
              key: "archives",
              header: "Archives",
              render: (workOrder) =>
                workOrder.archiveSearchTargets.map((target) => target.sourceId).join(", "),
            },
            {
              key: "gate",
              header: "Gate",
              render: (workOrder) => workOrder.acceptanceGates[2],
            },
            {
              key: "stop",
              header: "Stop condition",
              render: (workOrder) => workOrder.stopConditions.at(-1),
            },
          ]}
        />
      </EvidencePanel>
      <EvidencePanel title="Source-backed geometry candidates" status="composition blocked" className="panel-large">
        <p>{generatedSmallBodySourceCandidateLedger.operatingRule}</p>
        <DataTable
          shell="shell"
          rows={generatedSmallBodySourceCandidateLedger.sourceCandidates}
          getRowKey={(candidate) => candidate.id}
          columns={[
            { key: "designation", header: "Designation" },
            { key: "bestCloseApproachDate", header: "Best approach" },
            { key: "closeApproachCount", header: "Approaches" },
            {
              key: "distanceAu",
              header: "Distance AU",
              render: (candidate) => candidate.values.distanceAu ?? "unknown",
            },
            {
              key: "vInfinity",
              header: "v∞ km/s",
              render: (candidate) => candidate.values.vInfinityKms ?? candidate.values.vRelativeKms ?? "unknown",
            },
            { key: "geometryScore", header: "Geometry score" },
            {
              key: "allowedUse",
              header: "Allowed use",
              render: (candidate) => candidate.promotionGate.allowedUse,
            },
            {
              key: "blockers",
              header: "Blockers",
              render: (candidate) => candidate.promotionGate.blockers.join(" "),
            },
          ]}
        />
        <p>Ledger hash: {generatedSmallBodySourceCandidateLedger.ledgerHash}</p>
      </EvidencePanel>
      <EvidencePanel title="Candidate ranking" status="unverified" className="panel-large">
        <DataTable
          rows={scored}
          getRowKey={(candidate) => candidate.id}
          columns={[
            { key: "name", header: "Candidate" },
            {
              key: "score",
              header: "Score",
              render: (candidate) => <ScoreBarCell value={candidate.score} tone="blue" label={round(candidate.score, 1)} />,
            },
            {
              key: "vInfKms",
              header: "v∞ km/s",
              render: (candidate) => candidate.inputs.vInfKms,
            },
            {
              key: "orbitAccessibility",
              header: "Orbit access",
              render: (candidate) => candidate.inputs.orbitAccessibility,
            },
            {
              key: "resourceClassConfidence",
              header: "Resource confidence",
              render: (candidate) => candidate.inputs.resourceClassConfidence,
            },
            {
              key: "evidence",
              header: "Evidence",
              render: (candidate) =>
                candidate.evidence?.missingEvidence.length
                  ? candidate.evidence.missingEvidence.join(", ")
                  : "complete",
            },
            {
              key: "status",
              header: "Status",
              render: (candidate) => (
                <StatusPill tone={candidate.promotionBlocked ? "red" : "green"}>{candidate.status}</StatusPill>
              ),
            },
            { key: "note", header: "Note" },
          ]}
        />
      </EvidencePanel>
    </section>
  );
}
